import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { IdentityGuardedQueryClient } from '@papan/files/client/queries'
import { getBrandSnapshot } from '@papan/brand/client'
import { TooltipHost } from '@papan/core-ui/ui'
import { setBrandInterpolationVars } from '@papan/i18n'
import '@papan/i18n'
import { loadLandingExperience } from './lib/landing'
import { runOfficeProviderSelfHeal } from '@papan/collab/client'
import { createMutationCache } from './lib/mutationErrorBackstop'
import './index.css'
import './pdapp-card.css' // the PDApp card's look; after index.css (its behaviour rules)
import App from './App'
import { queryRetry } from './query-retry'
import { installSessionQueryReset } from './lib/sessionQueryReset'

// Feed the brand into i18n so resource strings that say `{{productName}}` /
// `{{productTagline}}` render the running brand, without every call site
// having to pass the values.
//
// Called ONCE, synchronously, with no subscription: the brand is baked into
// index.html at build time (apps/web/vite-plugin-brand.ts), so it is already
// final before this module loads. It used to be fetched here and subscribed to
// because it arrived after first paint — which is also what made a branded
// host flash the vendor's name.
const { identity } = getBrandSnapshot()
setBrandInterpolationVars({ productName: identity.productName, productTagline: identity.tagline })

// Which surface this deployment calls home. Unlike the brand above, this one is
// a runtime platform setting, so it has to be fetched — and the request is
// started HERE, outside `createRoot` and deliberately un-awaited, so it is
// already in flight before React mounts.
//
// Nothing waits on this promise. Both shells hold the boot skeleton they
// already render until `landingSettled()` (App.tsx / MobileShell.tsx), which is
// the only place the wait is expressed. Awaiting here instead would block the
// paint of every cold visit — including the ones that would have resolved
// correctly from cache — to fix a case that only arises when the runtime
// setting disagrees with the build default.
void loadLandingExperience()

// #633: if this bundle was built for a different office engine than the
// deployment now serves (a collabora ⇄ onlyoffice provider-change deploy under a
// live tab), the stale bundle mounts the wrong office editor and dead-ends on
// "Unable to open this file". Resolve the current provider and, on a mismatch,
// do one guarded reload to pull the fresh bundle — so users never have to know
// to hard-refresh. Un-awaited and fail-safe: a no-op unless a provider change
// actually shipped, and a no-op if the endpoint can't be reached.
void runOfficeProviderSelfHeal()

// Dev-only: forward console + WebSocket lifecycle from an un-inspectable device
// (iOS) to the Vite terminal. Opt-in per device via ?devlog=1. `import.meta.env.DEV`
// is statically false in production, so the whole block tree-shakes away.
if (import.meta.env.DEV) {
  void import('./dev/remoteLog').then((m) => m.installRemoteLog())
}

// Global backstop for a mutation whose call site forgot `onError` (#341 Case
// F) — see apps/web/src/lib/mutationErrorBackstop.ts for why this fires and
// why it's a DOM event rather than a toast call.
const mutationCache = createMutationCache()

// Defaults chosen for Papan's usage profile:
// - staleTime 30s: most reads (file lists, metadata) don't churn that fast
//   and dedup-via-cache is the whole point of this layer.
// - gcTime 5min: keep fetched data around long enough for back-navigation
//   without a refetch flash.
// - refetchOnWindowFocus off: returning to the tab shouldn't re-fan-out
//   200 requests; realtime invalidation will push diffs (Phase 4).
// - retry 1: quick fail; the user can always retry by interacting again.
// Per-query overrides live next to each query hook for non-default needs
// (auth/session: short stale; permissions: longer stale; etc.).
// #1210: every identity-scoped query runs behind the identity guard (`IdentityGuardedQueryClient`)
const queryClient = new IdentityGuardedQueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      retry: queryRetry, // one retry; never one refused for an identity change (#1210)
      refetchOnWindowFocus: false,
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: 0,
    },
  },
})

// #1316: an identity / account end removes the previous session's identity-scoped entries
installSessionQueryReset(queryClient)

// Dev-time online recovery: when the dev offline toggle flips back online,
// invalidate the entire cache so stale fetches retry. Browser 'online'
// events are already handled by refetchOnReconnect: 'always'.
if (typeof window !== 'undefined') {
  window.addEventListener('papan:online-recovery', () => {
    queryClient.invalidateQueries()
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {/* Hover hints for every `data-tooltip` element (toolbar icon buttons). */}
      <TooltipHost />
    </QueryClientProvider>
  </StrictMode>
)
